import { useState } from 'react';
import { ChartFrame, EmptyChart, INK, MotionStyles, moneyText, seriesColour, signedText, useMeasuredWidth, usePrefersReducedMotion, type ChartTable, type TooltipState } from './Chart';
import { linearScale, niceDomain } from './layout';

/**
 * Where a period's money went: the balance it started on, each kind of
 * money in and out as a step, and where it ended.
 *
 * ── LAID ON ITS SIDE ──
 * A waterfall is usually drawn upright, its steps along the bottom. Here the
 * steps are rows, each a bar floating from the running balance before it to
 * the one after, with its name above and its amount at the end. The names
 * are the point - "Customer payments", "Software subscriptions" - and an
 * upright waterfall on a phone has room under each bar for three letters of
 * one. On its side every name is whole at any width, the same choice
 * `HBars` made for the same reason.
 *
 * Money in is green and money out is red, and each also carries a sign in
 * its label, so the meaning survives a greyscale print. The two totals are
 * the theme's ink: they are balances, not movements.
 */

export interface WaterfallStep {
  key: string;
  label: string;
  /** Signed, in minor units: positive in, negative out. */
  minor: number;
}

export interface WaterfallProps {
  openingLabel: string;
  openingMinor: number;
  steps: WaterfallStep[];
  closingLabel: string;
  currency: string;
  title: string;
  summary: string;
  empty: { title: string; description?: string };
}

const ROW = 40;
const BAR = 14;

export function Waterfall({ openingLabel, openingMinor, steps, closingLabel, currency, title, summary, empty }: WaterfallProps) {
  const [frameRef, width] = useMeasuredWidth<HTMLDivElement>();
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState<number | null>(null);

  if (steps.length === 0 && openingMinor === 0) return <EmptyChart title={empty.title} description={empty.description} />;

  const closingMinor = openingMinor + steps.reduce((sum, s) => sum + s.minor, 0);
  // Each row: [from, to] on the axis. The totals run from zero.
  let running = openingMinor;
  const rows = [
    { key: 'opening', label: openingLabel, from: 0, to: openingMinor, kind: 'total' as const, minor: openingMinor },
    ...steps.map((step) => {
      const from = running;
      running += step.minor;
      return { key: step.key, label: step.label, from, to: running, kind: step.minor >= 0 ? ('in' as const) : ('out' as const), minor: step.minor };
    }),
    { key: 'closing', label: closingLabel, from: 0, to: closingMinor, kind: 'total' as const, minor: closingMinor },
  ];
  const lo = Math.min(0, ...rows.flatMap((r) => [r.from, r.to]));
  const hi = Math.max(0, ...rows.flatMap((r) => [r.from, r.to]));
  const longest = Math.max(...rows.map((r) => signedText(r.minor, currency).length));
  const right = Math.max(80, width - longest * 7 - 10);
  const x = linearScale(niceDomain(lo, hi === lo ? lo + 1 : hi, 4), [2, right]);
  const height = rows.length * ROW + 4;
  const colour = (kind: 'total' | 'in' | 'out') => (kind === 'total' ? INK.text : kind === 'in' ? seriesColour('green') : seriesColour('red'));
  const text = (row: (typeof rows)[number]) => (row.kind === 'total' ? moneyText(row.minor, currency) : signedText(row.minor, currency));

  const tooltipFor = (i: number): TooltipState => {
    const row = rows[i];
    if (!row) return { x: 0, y: 0, title: '', rows: [] };
    return {
      x: x(Math.max(row.from, row.to)),
      y: i * ROW + 18,
      title: row.label,
      rows: [
        { key: 'amount', label: row.kind === 'total' ? 'Balance' : row.kind === 'in' ? 'In' : 'Out', value: text(row) },
        ...(row.kind === 'total' ? [] : [{ key: 'after', label: 'Balance after', value: moneyText(row.to, currency) }]),
      ],
    };
  };

  const table: ChartTable = {
    caption: `${title}, in ${currency}`,
    columns: ['Step', 'Amount', 'Balance after'],
    rows: rows.map((row) => ({ key: row.key, cells: [row.label, text(row), moneyText(row.to, currency)] })),
  };

  const step = (direction: -1 | 1 | 'first' | 'last') =>
    setActive((current) => {
      if (direction === 'first') return 0;
      if (direction === 'last') return rows.length - 1;
      const next = (current ?? (direction === 1 ? -1 : rows.length)) + direction;
      return Math.max(0, Math.min(rows.length - 1, next));
    });

  return (
    <ChartFrame title={title} table={table} tooltip={active === null ? null : tooltipFor(active)} onStep={step} onLeave={() => setActive(null)} frameRef={frameRef}>
      <svg role="img" aria-label={summary} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="block select-none" data-chart-waterfall>
        <defs>
          <MotionStyles enabled={!reduced} />
        </defs>
        <line aria-hidden x1={x(0)} x2={x(0)} y1={0} y2={height} stroke={INK.zero} strokeWidth={1} shapeRendering="crispEdges" />
        {rows.map((row, i) => {
          const top = i * ROW + 20;
          const left = x(Math.min(row.from, row.to));
          const barWidth = Math.max(2, Math.abs(x(row.to) - x(row.from)));
          const next = rows[i + 1];
          return (
            <g
              key={row.key}
              onPointerEnter={(event) => {
                if (event.pointerType !== 'touch') setActive(i);
              }}
              onPointerLeave={(event) => {
                if (event.pointerType !== 'touch') setActive(null);
              }}
              onPointerDown={(event) => {
                if (event.pointerType === 'touch') setActive((current) => (current === i ? null : i));
              }}
            >
              <rect x={0} y={i * ROW} width={width} height={ROW} fill="transparent" />
              <text x={2} y={top - 6} fontSize={12} fill={row.kind === 'total' ? INK.text : INK.axis} fontWeight={row.kind === 'total' ? 600 : 400}>
                {row.label}
              </text>
              <rect
                x={left}
                y={top}
                width={barWidth}
                height={BAR}
                rx={2}
                fill={colour(row.kind)}
                opacity={row.kind === 'total' ? 0.85 : active === null || active === i ? 1 : 0.55}
                style={reduced ? undefined : { transformBox: 'fill-box', transformOrigin: 'left', animation: 'chart-grow-x 500ms ease-out both' }}
              >
                <title>{`${row.label}: ${text(row)}`}</title>
              </rect>
              <text x={Math.max(x(row.from), x(row.to)) + 6} y={top + BAR / 2} dy="0.35em" fontSize={12} fontWeight={500} fill={INK.text} className="tabular-nums">
                {text(row)}
              </text>
              {/* A hairline from this step's end to where the next begins: the balance carried down. */}
              {next && next.kind !== 'total' ? (
                <line aria-hidden x1={x(row.to)} x2={x(row.to)} y1={top + BAR} y2={top + ROW} stroke={INK.zero} strokeWidth={1} strokeDasharray="2 2" />
              ) : null}
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
}
